<template>
	<div class="border-separator-secondary rounded-xl border-2 p-0.5">
		<div class="overflow-hidden rounded-lg">
			<div
				class="bg-separator-secondary relative flex flex-col items-center px-3 py-5"
			>
				<div
					class="text-label-secondary absolute top-4 left-4 text-sm font-medium"
				>
					{{ chance }}%
				</div>
				<div
					class="text-label-secondary absolute top-1 right-1 p-3 text-sm font-medium"
					v-hover="{ active: 'scale-110', impact: 'soft' }"
					@click="share"
				>
					<ShareIcon class="h-5 w-5" v-if="!sharing" />
					<LoaderIcon class="h-5 w-5" v-else />
				</div>
				<svg
					width="346"
					height="220"
					viewBox="0 0 346 220"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
					class="absolute top-9.5 right-0 bottom-15 left-0"
				>
					<mask
						id="mask0_118_3773"
						style="mask-type: alpha"
						maskUnits="userSpaceOnUse"
						x="0"
						y="0"
						width="346"
						height="220"
					>
						<rect
							x="346"
							y="220"
							width="346"
							height="220"
							transform="rotate(180 346 220)"
							fill="url(#paint0_radial_118_3773)"
						/>
					</mask>
					<g mask="url(#mask0_118_3773)">
						<path
							opacity="0.1"
							d="M-203.778 12.8105H548.407V14.4463H-203.778V12.8105ZM-203.778 20.5596H548.407V22.1953H-203.778V20.5596ZM-203.778 28.3096H548.407V29.9453H-203.778V28.3096ZM-203.778 36.0596H548.407V37.6953H-203.778V36.0596ZM-203.778 43.8096H548.407V45.4453H-203.778V43.8096ZM-203.778 51.5586H548.407V53.1943H-203.778V51.5586ZM-203.778 59.3086H548.407V60.9443H-203.778V59.3086ZM-203.778 67.0586H548.407V68.6943H-203.778V67.0586ZM-203.778 74.8086H548.407V76.4443H-203.778V74.8086ZM-203.778 82.5576H548.407V84.1934H-203.778V82.5576ZM-203.778 90.3076H548.407V91.9434H-203.778V90.3076ZM-203.778 98.0576H548.407V99.6934H-203.778V98.0576ZM-203.778 105.808H548.407V107.443H-203.778V105.808ZM-203.778 113.557H548.407V115.192H-203.778V113.557ZM-203.778 121.307H548.407V122.942H-203.778V121.307ZM-203.778 129.057H548.407V130.692H-203.778V129.057ZM-203.778 136.808H548.407V138.443H-203.778V136.808ZM-203.778 144.557H548.407V146.192H-203.778V144.557ZM-203.778 152.307H548.407V153.942H-203.778V152.307ZM-203.778 160.057H548.407V161.692H-203.778V160.057ZM-203.778 167.807H548.407V169.442H-203.778V167.807ZM-203.778 175.556H548.407V177.191H-203.778V175.556ZM-203.778 183.306H548.407V184.941H-203.778V183.306ZM-203.778 191.056H548.407V192.691H-203.778V191.056ZM-203.778 198.806H548.407V200.441H-203.778V198.806ZM-203.778 206.555H548.407V208.19H-203.778V206.555ZM-203.778 214.305H548.407V215.94H-203.778V214.305Z"
							fill="#EFB248"
						/>
					</g>
					<defs>
						<radialGradient
							id="paint0_radial_118_3773"
							cx="0"
							cy="0"
							r="1"
							gradientUnits="userSpaceOnUse"
							gradientTransform="translate(519 330) rotate(180) scale(173 109.718)"
						>
							<stop offset="0.246979" stop-color="#0A1119" />
							<stop offset="1" stop-color="#33567F" stop-opacity="0" />
						</radialGradient>
					</defs>
				</svg>
				<LogoText class="h-5" />
				<LazySticker
					class="mt-4 h-36 w-36"
					:src="src"
					autoplay
					loop
					:preloader="preloader || undefined"
				/>
				<div class="text-label-primary mt-4 text-sm font-medium uppercase">
					{{ collection }}
				</div>
				<div
					class="font-squad mt-0.5 text-xl font-black uppercase"
					:class="{
						'text-orange': color === 'orange',
						'text-pink-dark': color === 'red',
						'text-pink': color === 'pink',
						'text-blue': color === 'blue'
					}"
				>
					{{ name }}
				</div>
				<CoinPrice class="mt-3" :price="price" />
			</div>
			<div class="mt-px flex h-11 gap-px" v-if="!cannotBeSold">
				<RouterLink
					to="/inventory"
					custom
					v-slot="{ navigate }"
					v-if="!cannotBeClaimed"
				>
					<div
						class="bg-separator-secondary flex-1"
						:class="{ 'pointer-events-none': sold }"
						@click.stop="navigate()"
					>
						<div
							class="text-label-secondary flex h-full w-full items-center justify-center gap-1.5"
							:class="{ 'opacity-60': sold }"
							v-hover="{ active: 'scale-95', impact: 'soft' }"
						>
							<ClaimIcon class="h-5 w-5" />
							<div class="text-sm font-semibold uppercase">claim</div>
						</div>
					</div>
				</RouterLink>
				<div
					class="bg-separator-secondary flex-1"
					:class="{ 'pointer-events-none': sold }"
					@click.stop="sell()"
				>
					<div
						class="text-label-secondary flex h-full w-full items-center justify-center gap-1.5"
						:class="{ 'opacity-60': sold }"
						v-hover="{ active: 'scale-95', impact: 'soft' }"
					>
						<SellIcon class="h-5 w-5" />
						<div class="text-sm font-semibold uppercase">{{ sellText }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
<script setup lang="ts">
import LogoText from '@/assets/icons/logo-text.svg'
import LazySticker from './LazySticker.vue'
import CoinPrice from './CoinPrice.vue'
import ClaimIcon from '@/assets/icons/claim.svg'
import SellIcon from '@/assets/icons/sell.svg'
import { computed, ref } from 'vue'
import ShareIcon from '@/assets/icons/share.svg'
import LoaderIcon from '@/assets/icons/loader.svg'

const props = defineProps<{
	collection: string
	name: string
	src: string
	price: number
	chance: number
	color: 'red' | 'orange' | 'blue' | 'pink'
	sold?: boolean
	preloader: string | null
	cannotBeSold?: boolean
	cannotBeClaimed?: boolean
	sharing?: boolean
}>()

const emit = defineEmits<{
	(e: 'sell'): void
	(e: 'share'): void
}>()

const sell = () => {
	if (props.sold) {
		return
	}

	emit('sell')
}

const sellText = computed(() => {
	return props.sold ? 'sold' : `sell`
})

const share = async () => {
	if (props.sharing) {
		return
	}

	emit('share')
}
</script>
